import {
  Actions,
  Badge,
  BuildingLibraryIcon,
  Button,
  ChevronDownIcon,
  deleteFromDB,
  ErrorAlert,
  getBanks,
  Image,
  Input,
  Modal,
  modalHandler,
  ModalOpenBtn,
  NoData,
  PencilSquareIcon,
  success,
  SwitchInput,
  useCanShow,
  useCustomForm,
  useEffect,
  useRef,
  useState,
} from "@";
import {
  bankAccountsDefaultValues,
  bankAccountsSchema,
} from "./utils/validation";
import { saveBank } from "./utils/api";
import { accountsFilter, bankFillForm } from "./utils/helpers";
import BankAccountsSkeleton from "./components/BankAccountsSkeleton";
import EditBankModal from "./components/EditBankModal";

const BankAccounts = () => {
  const [data, setData] = useState([]);
  const [banks, setBanks] = useState([]);
  const [search, setSearch] = useState("");
  const [error, setError] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [openIds, setOpenIds] = useState(() => new Set());
  const filteredItems = accountsFilter(data, search);
  const canEditAssociationData = useCanShow("editAssociationData");
  const editBankRef = useRef();

  const {
    reset,
    register,
    errors,
    handleSubmit,
    isSubmitting,
    watch,
    setValue,
  } = useCustomForm(bankAccountsDefaultValues, bankAccountsSchema);

  const clearForm = () => {
    setIsEdit(false);
    reset(bankAccountsDefaultValues);
  };

  async function fetchData() {
    setIsLoading(true);
    try {
      const { data: banks } = await getBanks();
      setBanks(banks.data);
      setData(banks.data);
    } catch (error) {
      setError(true);
    } finally {
      setIsLoading(false);
    }
  }

  const submit = async (form) => {
    try {
      await saveBank(form);
      success("تم الحفظ بنجاح");
      modalHandler();
      fetchData();
      clearForm();
    } catch (error) {}
  };

  const handlerEdit = (item, bank) => {
    modalHandler();
    setIsEdit(true);
    reset(bankFillForm(item, bank));
  };

  const deleteHandler = async (id) => {
    const res = await deleteFromDB(id, "accounts");
    if (res) fetchData();
  };

  const toggleOpen = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const banksWithAccounts = filteredItems?.filter(
    (item) => item?.bankAccounts?.length > 0,
  );
  const totalAccounts =
    banksWithAccounts?.reduce(
      (sum, b) => sum + (b.bankAccounts?.length || 0),
      0,
    ) || 0;

  return (
    <>
      <ErrorAlert reload={fetchData} show={error} />

      {/* Add/Edit account modal */}
      <Modal
        close={clearForm}
        title={isEdit ? "تعديل الحساب" : "اضافة حساب"}
        description="هنا يمكنك اضافة او تعديل الحساب البنكي"
      >
        <form onSubmit={handleSubmit(submit)} className="relative p-4">
          <Input
            style="mb-5"
            label="الاسم"
            error={errors.name}
            input={register("name")}
          />
          <Input
            style="mb-5 banks-select"
            label="البنك"
            type="select"
            error={errors.bank}
            list={banks}
            input={register("bank")}
          />
          <Input
            style="mb-5"
            label="IBAN"
            error={errors.iban}
            input={register("iban")}
          />
          <div className="mb-5">
            <SwitchInput
              label="الحساب الافتراضي"
              checked={watch("isDefault")}
              change={(value) => setValue("isDefault", value)}
              isBordered={true}
            />
          </div>
          <Button isLoading={isSubmitting} type="submit" title="حفظ البيانات" />
        </form>
      </Modal>

      <EditBankModal ref={editBankRef} onSuccess={fetchData} />

      <main>
        {/* Toolbar */}
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-gray-100 bg-gradient-to-l from-purple-50/30 via-white to-white p-3 dark:border-gray-700/60 dark:from-purple-950/15 dark:via-gray-800 dark:to-gray-800">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-purple-800 to-purple-950 text-white shadow-md shadow-purple-900/25 ring-1 ring-purple-900/10 dark:from-purple-600 dark:to-purple-800">
              <BuildingLibraryIcon className="h-5 w-5" />
            </span>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-purple-700 dark:text-purple-400">
                الحسابات البنكية
              </p>
              <p className="text-sm font-medium tracking-tight text-gray-700 dark:text-gray-300">
                <span className="font-bold tabular-nums text-purple-900 dark:text-purple-300">
                  {banksWithAccounts?.length || 0}
                </span>{" "}
                بنك
                <span className="mx-2 text-gray-300 dark:text-gray-600">·</span>
                <span className="font-bold tabular-nums text-purple-900 dark:text-purple-300">
                  {totalAccounts}
                </span>{" "}
                حساب
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <input
              type="search"
              value={search}
              placeholder="بحث عن حساب"
              onChange={(e) => setSearch(e.target.value)}
              className="single-search-input"
            />
            {canEditAssociationData && <ModalOpenBtn title="اضافة حساب" />}
          </div>
        </div>

        {/* Bank list */}
        {isLoading ? (
          <BankAccountsSkeleton />
        ) : banksWithAccounts?.length > 0 ? (
          <div className="space-y-3">
            {banksWithAccounts.map((item) => {
              const isOpen = openIds.has(item.id);
              const accountsCount = item.bankAccounts?.length || 0;
              return (
                <div
                  key={item.id}
                  className={`group/bank relative overflow-hidden rounded-2xl border bg-white transition-all duration-200 dark:bg-gray-800 ${
                    isOpen
                      ? "border-purple-100 shadow-md shadow-purple-900/5 dark:border-purple-800/40"
                      : "border-gray-100 hover:border-gray-200 dark:border-gray-700/60 dark:hover:border-gray-600"
                  }`}
                >
                  {/* Start-side accent stripe when open */}
                  {isOpen && (
                    <span
                      aria-hidden
                      className="pointer-events-none absolute start-0 top-0 h-full w-1 bg-gradient-to-b from-purple-700 to-purple-950 dark:from-purple-400 dark:to-purple-700"
                    />
                  )}

                  {/* Header row */}
                  <div className="flex flex-wrap items-center justify-between gap-3 p-3 md:p-4">
                    <button
                      type="button"
                      onClick={() => toggleOpen(item.id)}
                      className="flex flex-1 items-center gap-3 rounded-2xl px-2 py-1 text-start transition-colors hover:bg-gray-50/70 dark:hover:bg-gray-700/30"
                    >
                      <ChevronDownIcon
                        className={`h-4 w-4 shrink-0 text-gray-400 transition-transform duration-300 dark:text-gray-500 ${
                          isOpen ? "rotate-180" : ""
                        }`}
                        strokeWidth={2.5}
                      />

                      <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white p-1 ring-1 ring-gray-100 dark:bg-gray-900 dark:ring-gray-700">
                        <Image
                          click={() => {}}
                          size={48}
                          src={item.imageFullUrl}
                        />
                      </span>

                      <div className="min-w-0 flex-1">
                        <h3 className="truncate text-base font-bold tracking-tight text-gray-900 dark:text-gray-100">
                          {item.name}
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-500">
                          <span className="font-semibold tabular-nums text-purple-700 dark:text-purple-400">
                            {accountsCount}
                          </span>{" "}
                          {accountsCount === 1 ? "حساب" : "حسابات"}
                        </p>
                      </div>
                    </button>

                    <div className="flex items-center gap-2">
                      <Badge
                        title={item.isActive ? "مفعل" : "غير مفعل"}
                        color={item.isActive ? "green" : "red"}
                      />
                      {canEditAssociationData && (
                        <button
                          type="button"
                          onClick={() => editBankRef.current?.open(item)}
                          aria-label="تعديل البنك"
                          title="تعديل البنك"
                          className="group/edit flex h-9 w-9 items-center justify-center rounded-lg border border-gray-100 bg-white text-gray-500 transition-all duration-200 hover:border-purple-200 hover:bg-purple-50 hover:text-purple-900 active:scale-95 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:hover:border-purple-700 dark:hover:bg-gray-700 dark:hover:text-purple-300"
                        >
                          <PencilSquareIcon className="h-4 w-4 transition-transform group-hover/edit:-rotate-12" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Accounts grid (collapsible) */}
                  <div
                    className={`grid transition-all duration-300 ease-out ${
                      isOpen
                        ? "grid-rows-[1fr] opacity-100"
                        : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="border-t border-gray-100 bg-gradient-to-br from-gray-50/40 to-white px-3 py-3 dark:border-gray-700/60 dark:from-gray-900/40 dark:to-gray-800 md:px-4">
                        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                          {item.bankAccounts.map((account) => (
                            <div
                              key={account.id}
                              className={`group/acc relative flex items-center justify-between gap-2 overflow-hidden rounded-2xl border bg-white p-3 transition-all duration-200 dark:bg-gray-800/60 ${
                                account.isDefault
                                  ? "border-purple-200 ring-1 ring-purple-100 dark:border-purple-700/50 dark:ring-purple-900/30"
                                  : "border-gray-100 hover:border-gray-200 dark:border-gray-700/60 dark:hover:border-gray-600"
                              }`}
                            >
                              {account.isDefault && (
                                <span
                                  aria-hidden
                                  className="pointer-events-none absolute start-0 top-0 h-full w-0.5 bg-gradient-to-b from-purple-500 to-purple-700 dark:from-purple-400 dark:to-purple-600"
                                />
                              )}
                              <div className="min-w-0 flex-1">
                                <h4 className="truncate text-sm font-semibold tracking-tight text-gray-900 dark:text-gray-100">
                                  {account.name}
                                </h4>
                                {account.isDefault && (
                                  <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-purple-50 px-2 py-0.5 text-[10px] font-bold text-purple-900 ring-1 ring-purple-100 dark:bg-purple-950/40 dark:text-purple-300 dark:ring-purple-800/40">
                                    <span
                                      aria-hidden
                                      className="h-1 w-1 rounded-full bg-current"
                                    />
                                    الحساب الافتراضي
                                  </span>
                                )}
                              </div>

                              {canEditAssociationData && (
                                <Actions
                                  notShow
                                  notDelete={account?.isDefault}
                                  row={account}
                                  edit={() => handlerEdit(account, item.id)}
                                  delete={deleteHandler}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <NoData />
        )}
      </main>
    </>
  );
};

export default BankAccounts;
